import React, { useState, useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { SignIn, UserButton, useUser, useAuth } from '@clerk/clerk-react';
import { Plus, Edit2, Trash2, Save, X, Check, AlertCircle, Clock,BarChart3 } from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import AnalyticsDashboard from './AnalyticsDashboard';

const PartnerPortal = () => {
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  
  const [facilityId, setFacilityId] = useState(null);
  const [facilityData, setFacilityData] = useState(null);
  const [units, setUnits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingUnit, setEditingUnit] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showHoursForm, setShowHoursForm] = useState(false);
  const [operatingHours, setOperatingHours] = useState({
    monday: { open: '09:00', close: '17:00', closed: false },
    tuesday: { open: '09:00', close: '17:00', closed: false },
    wednesday: { open: '09:00', close: '17:00', closed: false },
    thursday: { open: '09:00', close: '17:00', closed: false },
    friday: { open: '09:00', close: '17:00', closed: false },
    saturday: { open: '09:00', close: '17:00', closed: false },
    sunday: { open: '09:00', close: '17:00', closed: true }
  });
  const [newUnit, setNewUnit] = useState({
    unit_number: '',
    size_sqm: '',
    price_monthly: '',
    status: 'available',
    features: [],
    floor_level: 0
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(false); 

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (isSignedIn) {
      loadFacilityData();
    }
  }, [isSignedIn]);

  const loadFacilityData = async () => {
    try {
      const token = await getToken();
      
      const authResponse = await fetch(`${API_URL}/api/partner/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const authData = await authResponse.json();
      const userFacilityId = authData.facility_id;
      
      if (!userFacilityId) {
        alert('No facility assigned to your account. Please contact support.');
        setLoading(false);
        return;
      }
      
      setFacilityId(userFacilityId);
      
      const facilityResponse = await fetch(`${API_URL}/api/partner/facilities/${userFacilityId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const facilityData = await facilityResponse.json();
      setFacilityData(facilityData.facility);
      setUnits(facilityData.units);
      
      const statsResponse = await fetch(`${API_URL}/api/partner/facilities/${userFacilityId}/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const statsData = await statsResponse.json();
      setStats(statsData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading facility data:', error);
      setLoading(false);
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit({...unit, features: unit.features || []});
  };

  const handleSaveEdit = async () => {
    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/partner/units/${editingUnit.unit_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          price_monthly: editingUnit.price_monthly,
          status: editingUnit.status,
          features: editingUnit.features,
          size_sqm: editingUnit.size_sqm,
          floor_level: editingUnit.floor_level
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUnits(units.map(u => 
          u.unit_id === editingUnit.unit_id ? editingUnit : u
        ));
        setEditingUnit(null);
        showSuccessMessage('Unit updated successfully');
        loadFacilityData();
      } else {
        alert('Error updating unit: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving unit:', error);
      alert('Error saving unit. Please try again.');
    }
  };

  const handleDelete = async (unitId) => {
    if (!window.confirm(`Delete this unit?`)) return;
    
    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/partner/units/${unitId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUnits(units.filter(u => u.unit_id !== unitId));
        showSuccessMessage('Unit deleted');
        loadFacilityData();
      } else {
        alert('Error deleting unit: ' + data.error);
      }
    } catch (error) {
      console.error('Error deleting unit:', error);
      alert('Error deleting unit. Please try again.');
    }
  };

  const handleAddUnit = async () => {
    if (!newUnit.unit_number || !newUnit.size_sqm || !newUnit.price_monthly) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/partner/units/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          facility_id: facilityId,
          ...newUnit
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNewUnit({
          unit_number: '',
          size_sqm: '',
          price_monthly: '',
          status: 'available',
          features: [],
          floor_level: 0
        });
        setShowAddForm(false);
        showSuccessMessage('Unit added successfully');
        loadFacilityData();
      } else {
        alert('Error adding unit: ' + data.error);
      }
    } catch (error) {
      console.error('Error adding unit:', error);
      alert('Error adding unit. Please try again.');
    }
  };

  const showSuccessMessage = (msg) => {
    setSaveMessage(msg);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  if (!isSignedIn) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter', -apple-system, sans-serif"
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          maxWidth: '400px',
          width: '100%'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '800',
            marginBottom: '8px',
            textAlign: 'center'
          }}>
            Partner Portal
          </h1>
          <p style={{
            color: '#6b7280',
            fontSize: '14px',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            Sign in to manage your storage facility
          </p>
          <SignIn />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6b7280' }}>Loading your facility data...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f3f4f6',
      fontFamily: "'Inter', -apple-system, sans-serif",
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h1 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: '#1a1a1a',
                marginBottom: '4px'
              }}>
                Partner Portal
              </h1>
              <p style={{
                color: '#6b7280',
                fontSize: '14px'
              }}>
                {facilityData?.name || 'Loading...'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {user?.publicMetadata?.role === 'admin' && (
                <Link to="/admin" style={{
                  background: '#FEF3C7',
                  color: '#92400E',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Admin Dashboard
                </Link>
              )}
              <div style={{
                background: '#f3f4f6',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {user?.primaryEmailAddress?.emailAddress}
              </div>
              <UserButton />
            </div>
          </div>

          {saveMessage && (
            <div style={{
              marginTop: '16px',
              background: '#d1fae5',
              border: '1px solid #10b981',
              color: '#065f46',
              padding: '12px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              <Check size={18} />
              {saveMessage}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '16px 24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          gap: '12px'
        }}>
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <Plus size={18} />
            Add Unit
          </button>
          <button
            onClick={() => setShowHoursForm(true)}
            style={{
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <Clock size={18} />
            Operating Hours
          </button>
          <button
            onClick={loadFacilityData}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <Save size={18} />
            Refresh Data
          </button>
          <button
            onClick={() => setShowAnalytics(true)}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <BarChart3 size={18} />
            Analytics
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                Total Units
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#1a1a1a' }}>
                {stats.total_units}
              </div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                Available
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#10b981' }}>
                {stats.available_units}
              </div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                Occupied
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#ef4444' }}>
                {stats.occupied_units}
              </div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                Occupancy Rate
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#3b82f6' }}>
                {stats.occupancy_rate}%
              </div>
            </div>
          </div>
        )}

        {/* Add Unit Form */}
        {showAddForm && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '2px solid #667eea'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700' }}>Add New Unit</h3>
              <button
                onClick={() => setShowAddForm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} color="#6b7280" />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Unit Number *
                </label>
                <input
                  value={newUnit.unit_number}
                  onChange={(e) => setNewUnit({...newUnit, unit_number: e.target.value})}
                  placeholder="A-108"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Size (sqm) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={newUnit.size_sqm}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (value >= 0 || e.target.value === '') {
                      setNewUnit({...newUnit, size_sqm: e.target.value === '' ? '' : value});
                    }
                  }}
                  placeholder="15"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Price/Month ($) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newUnit.price_monthly}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (value >= 0 || e.target.value === '') {
                      setNewUnit({...newUnit, price_monthly: e.target.value === '' ? '' : value});
                    }
                  }}
                  placeholder="250"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Floor Level
                </label>
                <input
                  type="number"
                  min="0"
                  value={newUnit.floor_level}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 0 || e.target.value === '') {
                      setNewUnit({...newUnit, floor_level: e.target.value === '' ? 0 : value});
                    }
                  }}
                  placeholder="0"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <button
              onClick={handleAddUnit}
              style={{
                marginTop: '16px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Add Unit
            </button>
          </div>
        )}

        {/* Operating Hours Form */}
        {showHoursForm && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '2px solid #f59e0b'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700' }}>Operating Hours</h3>
              <button
                onClick={() => setShowHoursForm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} color="#6b7280" />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              {Object.keys(operatingHours).map(day => (
                <div key={day} style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr 1fr 100px',
                  gap: '16px',
                  alignItems: 'center',
                  padding: '12px',
                  background: operatingHours[day].closed ? '#f9fafb' : 'white',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    textTransform: 'capitalize',
                    color: operatingHours[day].closed ? '#9ca3af' : '#1f2937'
                  }}>
                    {day}
                  </div>
                  
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                      Opens
                    </label>
                    <input
                      type="time"
                      value={operatingHours[day].open}
                      onChange={(e) => setOperatingHours({
                        ...operatingHours,
                        [day]: { ...operatingHours[day], open: e.target.value }
                      })}
                      disabled={operatingHours[day].closed}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                        opacity: operatingHours[day].closed ? 0.5 : 1
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                      Closes
                    </label>
                    <input
                      type="time"
                      value={operatingHours[day].close}
                      onChange={(e) => setOperatingHours({
                        ...operatingHours,
                        [day]: { ...operatingHours[day], close: e.target.value }
                      })}
                      disabled={operatingHours[day].closed}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                        opacity: operatingHours[day].closed ? 0.5 : 1
                      }}
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: operatingHours[day].closed ? '#ef4444' : '#10b981'
                    }}>
                      <input
                        type="checkbox"
                        checked={operatingHours[day].closed}
                        onChange={(e) => setOperatingHours({
                          ...operatingHours,
                          [day]: { ...operatingHours[day], closed: e.target.checked }
                        })}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      {operatingHours[day].closed ? 'Closed' : 'Open'}
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={async () => {
                try {
                  const token = await getToken();
                  const response = await fetch(`${API_URL}/api/partner/facilities/${facilityId}/hours`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ operating_hours: operatingHours })
                  });
                  
                  const data = await response.json();
                  if (data.success) {
                    showSuccessMessage('Operating hours updated successfully');
                    setShowHoursForm(false);
                  } else {
                    alert('Error updating hours: ' + data.error);
                  }
                } catch (error) {
                  console.error('Error updating hours:', error);
                  alert('Error updating hours. Please try again.');
                }
              }}
              style={{
                marginTop: '16px',
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Save Operating Hours
            </button>

            <div style={{
              marginTop: '16px',
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              padding: '12px',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              <strong>Note:</strong> These hours will be displayed to customers when they search. 
              If closed on certain days, customers will see "Closed on [day]" in search results.
            </div>
          </div>
        )}

        {/* Units Table */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700' }}>Unit Inventory ({units.length})</h3>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Unit</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Size</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Price/Month</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Status</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Floor</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Features</th>
                  <th style={{ padding: '12px 24px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    {editingUnit?.unit_id === unit.unit_id ? (
                      // Editing Mode
                      <>
                        <td style={{ padding: '16px 24px' }}>
                          <span style={{ fontWeight: '700', fontSize: '14px' }}>
                            {unit.unit_number}
                          </span>
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={editingUnit.size_sqm}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (value >= 0 || e.target.value === '') {
                                setEditingUnit({...editingUnit, size_sqm: e.target.value === '' ? '' : value});
                              }
                            }}
                            style={{
                              padding: '6px 10px',
                              border: '2px solid #667eea',
                              borderRadius: '4px',
                              fontSize: '14px',
                              width: '80px'
                            }}
                          />
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editingUnit.price_monthly}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (value >= 0 || e.target.value === '') {
                                setEditingUnit({...editingUnit, price_monthly: e.target.value === '' ? '' : value});
                              }
                            }}
                            style={{
                              padding: '6px 10px',
                              border: '2px solid #667eea',
                              borderRadius: '4px',
                              fontSize: '14px',
                              width: '100px'
                            }}
                          />
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <select
                            value={editingUnit.status}
                            onChange={(e) => setEditingUnit({...editingUnit, status: e.target.value})}
                            style={{
                              padding: '6px 10px',
                              border: '2px solid #667eea',
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}
                          >
                            <option value="available">Available</option>
                            <option value="occupied">Occupied</option>
                            <option value="reserved">Reserved</option>
                          </select>
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <input
                            type="number"
                            value={editingUnit.floor_level}
                            onChange={(e) => setEditingUnit({...editingUnit, floor_level: parseInt(e.target.value)})}
                            style={{
                              padding: '6px 10px',
                              border: '2px solid #667eea',
                              borderRadius: '4px',
                              fontSize: '14px',
                              width: '60px'
                            }}
                          />
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>
                            {Array.isArray(editingUnit.features) ? editingUnit.features.join(', ') : editingUnit.features}
                          </span>
                        </td>
                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={handleSaveEdit}
                              style={{
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '700'
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingUnit(null)}
                              style={{
                                background: '#6b7280',
                                color: 'white',
                                border: 'none',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '700'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      // View Mode
                      <>
                        <td style={{ padding: '16px 24px', fontWeight: '700', fontSize: '14px' }}>
                          {unit.unit_number}
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px' }}>
                          {unit.size_sqm}sqm
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px', fontWeight: '600', color: '#10b981' }}>
                          ${unit.price_monthly}
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '700',
                            background: unit.status === 'available' ? '#d1fae5' : unit.status === 'occupied' ? '#fee2e2' : '#fef3c7',
                            color: unit.status === 'available' ? '#065f46' : unit.status === 'occupied' ? '#991b1b' : '#92400e'
                          }}>
                            {unit.status}
                          </span>
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px' }}>
                          {unit.floor_level === 0 ? 'Ground' : `Level ${unit.floor_level}`}
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {(Array.isArray(unit.features) ? unit.features : []).map((f, i) => (
                              <span
                                key={i}
                                style={{
                                  background: '#e0e7ff',
                                  color: '#4f46e5',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  fontSize: '11px',
                                  fontWeight: '600'
                                }}
                              >
                                {f}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleEdit(unit)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px'
                              }}
                            >
                              <Edit2 size={16} color="#667eea" />
                            </button>
                            <button
                              onClick={() => handleDelete(unit.unit_id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px'
                              }}
                            >
                              <Trash2 size={16} color="#ef4444" />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Help Section */}
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '12px',
          padding: '20px',
          marginTop: '24px'
        }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <AlertCircle size={20} color="#92400e" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#92400e', marginBottom: '8px' }}>
                Keep Your Inventory Updated
              </h4>
              <p style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.5' }}>
                Update your units whenever availability changes. This ensures customers see accurate information and helps you get more bookings.
                All changes sync to StorageScout instantly.
              </p>
            </div>
          </div>
        </div>
      </div>
{showAnalytics && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#f9fafb',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '1400px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowAnalytics(false)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              <X size={20} />
            </button>

            <div style={{ padding: '40px' }}>
              <AnalyticsDashboard facilityId={facilityId} />
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const App = () => {
  return (
    <Routes>
      <Route path="/" element={<PartnerPortal />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  );
};

export default App;
